import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../../components/ui/Modal';
import { Study, Resort, FormTemplate } from '../../../types/Rows';
import { fetchStudies, fetchStudy, fetchTemplatesByStudy } from '../../../api/studyApi';
import { addPlannedReport, AddPlannedReportDto } from '../../../api/reportsApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface NewReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onReportCreated?: () => void;
  fixedStudyId?: string;
}

export const NewReportModal: React.FC<NewReportModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  onReportCreated,
  fixedStudyId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [selectedResort, setSelectedResort] = useState<Resort | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [visitDate, setVisitDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportName, setReportName] = useState<string>('');
  const [participants, setParticipants] = useState<string>('');
  const [isRemote, setIsRemote] = useState<boolean>(false);

  // Fetch studies list
  const { data: studies, isLoading: studiesLoading } = useQuery(
    ['studies'],
    fetchStudies,
    {
      enabled: isOpen,
    }
  );

  // Fetch detailed study data when a study is selected
  const { data: detailedStudy, isLoading: detailedStudyLoading } = useQuery(
    ['study', selectedStudy?.id],
    () => fetchStudy(selectedStudy!.id),
    {
      enabled: !!selectedStudy?.id,
    }
  );

  // Fetch templates for the selected study
  const { data: templates, isLoading: templatesLoading } = useQuery(
    ['templates', selectedStudy?.id],
    () => fetchTemplatesByStudy(selectedStudy!.id),
    {
      enabled: !!selectedStudy?.id,
    }
  );

  // Create planned report mutation
  const createReportMutation = useMutation(
    (data: AddPlannedReportDto) => addPlannedReport(data),
    {
      onSuccess: () => {
        queryClient.resetQueries(['calendar-reports']);
        queryClient.resetQueries(['calendar-planned']);
        onReportCreated?.();
        handleClose();
      },
      onError: (error) => {
        console.error('Error creating planned report:', error);
      },
    }
  );

  // Set initial date when modal opens
  useEffect(() => {
    if (isOpen && selectedDate) {
      // Format date in local timezone to avoid day shift
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      setVisitDate(dateString);
    }
  }, [isOpen, selectedDate]);

  // Preselect study when fixedStudyId is provided
  useEffect(() => {
    if (isOpen && fixedStudyId && studies) {
      const study = studies.find((s) => s.id === fixedStudyId);
      if (study) {
        setSelectedStudy(study);
      }
    }
  }, [isOpen, fixedStudyId, studies]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedStudy(null);
      setSelectedResort(null);
      setSelectedTemplate(null);
      setVisitDate('');
      setEndDate('');
      setReportName('');
      setParticipants('');
      setIsRemote(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleStudyChange = (study: Study) => {
    setSelectedStudy(study);
    setSelectedResort(null);
    setSelectedTemplate(null);
  };

  const handleCreateReport = async () => {
    // if (!selectedStudy || !selectedResort || !visitDate) {
    //   return;
    // }

    try{
    const plannedReportData: AddPlannedReportDto = {
      templateId: selectedTemplate?.id,
      studyId: selectedStudy?.id,
      resortId: selectedResort?.id,
      dateStart: visitDate,
      dateEnd: endDate || undefined,
      isRemote:isRemote,
      name: reportName || undefined,
      participants: participants || undefined,
    };
    console.log('plannedReportData', plannedReportData);

    await createReportMutation.mutate(plannedReportData);
    handleClose();
  }
  catch(ex){
console.log('error', ex);
  }
  };

  const canCreate = selectedStudy && selectedResort && visitDate;

  // Get resorts from detailed study data and templates from dedicated endpoint
  const availableResorts = detailedStudy?.resorts || [];
  const availableTemplates = templates || [];

  return (
    <Modal open={isOpen} onClose={handleClose} size="md">
      <ModalHeader 
        title={t('planNewVisit')} 
        onClose={handleClose}
      />
      <ModalBody>
        <div className="space-y-4">
          {/* Visit Date */}
          <div>
            <label htmlFor="visitDate" className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
              {t('visitDate')}
            </label>
            <input
              type="date"
              id="visitDate"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              className="block w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
            />
          </div>

          {/* End Date (Optional) */}
          {/* <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              {t('visitDateEnd')} <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={visitDate}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div> */}

          {/* Report Name (Optional) */}
          <div>
            <label htmlFor="reportName" className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
              Nazwa <span className="normal-case text-[var(--sc-muted)] text-[10px]">(opcjonalnie)</span>
            </label>
            <input
              type="text"
              id="reportName"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Nazwa"
              className="block w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
            />
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="isRemote"
              checked={isRemote}
              onChange={(e) => setIsRemote(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--sc-border)] text-[#F4A832] focus:ring-[var(--sc-amber)]"
            />
            <label htmlFor="isRemote" className="ml-2 block text-sm text-[var(--sc-muted)]">
              {t('Remote')}
            </label>
          </div>


          {/* Participants (Optional) */}
          {/* <div>
            <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-1">
              {t('croParticipants')} <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <textarea
              id="participants"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
              placeholder="Enter participants"
              rows={2}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div> */}

      
          {/* Study Selection */}
          <div>
            <label htmlFor="study" className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
              {t('selectStudy')}
            </label>
            {studiesLoading ? (
              <div className="text-sm text-[var(--sc-muted)]">Loading studies...</div>
            ) : (
              <select
                id="study"
                value={selectedStudy?.id || ''}
                onChange={(e) => {
                  const study = studies?.find(s => s.id === e.target.value);
                  if (study) handleStudyChange(study);
                }}
                className="block w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
                disabled={!!fixedStudyId}
              >
                <option value="">{t('selectStudy')}</option>
                {studies?.filter(x=>x.name)         
                         .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase(), undefined, {
          numeric: true,
          sensitivity: "base",
        }))
                         .map((study) => (
                  <option key={study.id} value={study.id}>
                    {study.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Resort Selection */}
          {selectedStudy && (
            <div>
              <label htmlFor="resort" className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-1">
                {t('selectResort')}
              </label>
              {detailedStudyLoading ? (
                <div className="text-sm text-[var(--sc-muted)]">Loading resorts...</div>
              ) : (
                <select
                  id="resort"
                  value={selectedResort?.id || ''}
                  onChange={(e) => {
                    const resort = availableResorts.find(r => r.id === e.target.value);
                    if (resort) {
                      setSelectedResort(resort);
                      setSelectedTemplate(null);
                    }
                  }}
                  className="block w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
                  disabled={detailedStudyLoading}
                >
                  <option value="">{t('selectResort')}</option>
                  {availableResorts                       
                    .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase(), undefined, {
          numeric: true,
          sensitivity: "base",
        }))
                    .map((resort) => (
                    <option key={resort.id} value={resort.id}>
                      {resort.name} - {resort.address}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Template Selection */}
          {/* {selectedStudy && (
            <div>
              <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                {t('selectTemplate')}
              </label>
              {templatesLoading ? (
                <div className="text-sm text-gray-500">Loading templates...</div>
              ) : (
                <select
                  id="template"
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = availableTemplates.find(t => t.id === e.target.value);
                    if (template) setSelectedTemplate(template);
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  disabled={templatesLoading}
                >
                  <option value="">{t('selectTemplate')}</option>
                  {availableTemplates                         .sort((a, b) => (a.name.localeCompare( b.name)))
.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )} */}

          {/* Is Remote Checkbox */}
       
          {/* Selected Summary */}
          {/* {canCreate && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Summary:</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div><strong>{t('visitDate')}:</strong> {visitDate}</div>
                {endDate && <div><strong>{t('visitDateEnd')}:</strong> {endDate}</div>}
                <div><strong>{t('study')}:</strong> {selectedStudy?.name}</div>
                <div><strong>Resort:</strong> {selectedResort?.name}</div>
                <div><strong>Template:</strong> {selectedTemplate?.name}</div>
                {reportName && <div><strong>Report Name:</strong> {reportName}</div>}
                {participants && <div><strong>{t('croParticipants')}:</strong> {participants}</div>}
              </div>
            </div>
          )} */}
        </div>
      </ModalBody>

      <ModalFooter
        onCancel={handleClose}
        cancelLabel={t('cancel')}
        onSave={handleCreateReport}
        saveLabel={createReportMutation.isLoading ? t('creatingReport') : t('create')}
        saveDisabled={!canCreate}
        saveLoading={createReportMutation.isLoading}
      />
    </Modal>
  );
};
